import { useLayoutEffect, useRef, type RefObject } from "react";

// TaskDialog is at its size ceiling, so it imports the hook and this panel
// on one line. The panel lives in its own file so this hook file stays free
// of JSX.
export { TrappedPanel } from "../components/ui/TrappedPanel";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const DIALOG_SELECTOR = "[role='dialog'], [role='alertdialog']";

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => el.offsetParent !== null);
}

// LIFO of mounted panels, matching useEscapeKey: one document-level capture
// listener so Tab is claimed by the topmost dialog even when focus has left
// the panel (backdrop click, browser chrome). A panel-level listener never
// sees those keydowns.
interface FocusEntry {
  panel: HTMLElement;
  /** The control that opened this dialog. A replacement with the same session
   * (next/previous task) copies it so close still returns there. */
  trigger: HTMLElement | null;
  sessionId?: string;
}

const focusStack: FocusEntry[] = [];

/** Search and the shortcut list are dialogs, but they never join this stack.
 * Tab inside one of them must stay there instead of jumping back to the
 * dialog underneath. A dialog this panel contains (the tag list, for example)
 * still belongs to this panel's cycle. */
function focusIsInForeignDialog(panel: HTMLElement): boolean {
  const active = document.activeElement;
  if (!(active instanceof Element)) return false;
  const owner = active.closest(DIALOG_SELECTOR);
  return !!owner && owner !== panel && !panel.contains(owner);
}

function wrapTab(e: KeyboardEvent, panel: HTMLElement): void {
  if (focusIsInForeignDialog(panel)) return;

  const focusable = getFocusable(panel);
  if (focusable.length === 0) {
    e.preventDefault();
    e.stopImmediatePropagation();
    panel.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const inPanel = panel.contains(document.activeElement);
  if (e.shiftKey) {
    if (!inPanel || document.activeElement === first) {
      e.preventDefault();
      e.stopImmediatePropagation();
      last.focus();
    }
  } else if (!inPanel || document.activeElement === last) {
    e.preventDefault();
    e.stopImmediatePropagation();
    first.focus();
  }
}

function dispatchTab(e: KeyboardEvent): void {
  if (e.key !== "Tab") return;
  const top = focusStack[focusStack.length - 1];
  if (!top || !top.panel.isConnected) return;
  wrapTab(e, top.panel);
}

function pushFocusEntry(entry: FocusEntry): void {
  if (focusStack.length === 0) {
    document.addEventListener("keydown", dispatchTab, true);
  }
  focusStack.push(entry);
}

function removeFocusEntry(entry: FocusEntry): void {
  const i = focusStack.lastIndexOf(entry);
  if (i !== -1) focusStack.splice(i, 1);
  if (focusStack.length === 0) {
    document.removeEventListener("keydown", dispatchTab, true);
  }
}

/**
 * Keyboard accessibility for a dialog panel: move focus in on mount, trap Tab
 * to the topmost mounted panel, restore focus to the trigger on unmount.
 *
 * Trigger is captured during render, not in an effect: a panel with `autoFocus`
 * wins the race after paint, so an effect would record the dialog's own field
 * as the trigger instead of the button that opened it.
 *
 * `sessionId` ties a remounted panel to the dialog it replaces. Next/previous
 * task remounts the task dialog while focus is still inside the old one, and
 * choosing another task from Search does the same, because focus is in that
 * dialog rather than on a new card. Either way the new mount keeps the
 * original opener. A fresh click outside any dialog starts a new opener.
 */
export function useDialogFocus(
  panelRef: RefObject<HTMLElement | null>,
  sessionId?: string,
): void {
  const triggerRef = useRef<HTMLElement | null | undefined>(undefined);
  if (triggerRef.current === undefined) {
    const active =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const top = focusStack[focusStack.length - 1];
    const insideTop = !!top && !!active && top.panel.contains(active);
    const insideAnotherDialog =
      !!active &&
      active.closest(DIALOG_SELECTOR) !== null &&
      !insideTop;
    const replacing =
      !!sessionId &&
      !!top &&
      top.sessionId === sessionId &&
      (insideTop || insideAnotherDialog);
    triggerRef.current = replacing ? top.trigger : active;
  }

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const trigger = triggerRef.current ?? null;

    if (!panel.contains(document.activeElement)) {
      const [first] = getFocusable(panel);
      (first ?? panel).focus();
    }

    const entry: FocusEntry = { panel, trigger, sessionId };
    pushFocusEntry(entry);
    return () => {
      removeFocusEntry(entry);
      if (trigger && document.body.contains(trigger)) {
        trigger.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount; panelRef is a stable caller ref
  }, []);
}
